import { Modal } from "react-bootstrap";
import { siteService } from "../../Services/siteService";
import toast from "react-hot-toast";
import { useContext, useEffect, useRef, useState } from "react";
import { UserContext } from "../../Contexts/UserContext";
import DDLogoDark from '../../Assets/img/logo-dd-dark.svg';
import styles from './ContactUsModal.module.css';

const ContactUsModal = (props) => {
    const { user, showContactUsModal } = useContext(UserContext);
    const [userAlert, setUserAlert] = useState('');
    const [response, setResponse] = useState({
        id: 0,
        guid: null
    });
    const responseRef = useRef(response);
    const reCaptchaV3SiteKey = window.config["reCaptchaV3SiteKey"];
    const reCaptchaV2SiteKey = window.config["reCaptchaV2SiteKey"];
    const captchaRef = useRef(null);

    const initialValues = {
        firstname: user.name ?? user.profile?.name ?? '',
        industry: '',
        organizationType: '',
        designation: '',
        email: user.email ?? user.profile?.communication_email ?? '',
        phone: user.phoneNo ?? user.profile?.communication_phone_number ?? '',
        message: props.contactUsModal.message ?? '',
        captcha: ''
    };

    const [values, setValues] = useState({...initialValues});

    useEffect(() => {
        setValues({...initialValues});
    }, [user]);

    useEffect(() => {
        responseRef.current = response;
    }, [response]);

    useEffect(() => {
        loadRecaptchaScript("v3");
    }, []);

    const loadRecaptchaScript = (version) => {
        return new Promise((resolve, reject) => {
            if (window.grecaptcha) 
                return resolve(window.grecaptcha);

            const existingScript = document.querySelector("#recaptcha-script");
            if (existingScript) {
                existingScript.remove();
            }

            const script = document.createElement("script");
            script.id = "recaptcha-script";

            if (version === "v3") {
                script.src = `https://www.google.com/recaptcha/api.js?render=${reCaptchaV3SiteKey}`;
            } else {
                script.src = "https://www.google.com/recaptcha/api.js";
            }

            script.async = true;
            script.defer = true;
            script.onload = () => resolve(window.grecaptcha);
            script.onerror = reject;

            document.body.appendChild(script);
        });
    }

    const handleInputChange = (event) => {
        const {name, value} = event.target;
        setValues({
            ...values,
            [name]: value
        });
    }

    const handleSubmit  = () => {
        setUserAlert('');
        if (!values.firstname.trim()) {
            setUserAlert('Please enter the name');
            return;
        }
        else if(!values.email.trim() && !values.phone.trim()) {
            setUserAlert('Please enter a valid email id or phone number');
            return;
        }
        else if(values.email.trim() && ! (/(.+)@(.+){2,}\.(.+){2,}/.test(values.email.trim()) )) {
            setUserAlert('Please enter a valid email id');
            return;
        }
        else if(values.industry == "") {
            setUserAlert("Please select Industry");
            return;
        }
        else if(values.organizationType == "") {
            setUserAlert("Please select Organization Type");
            return;
        }
        else if(values.designation == "") {
            setUserAlert("Please select Role/Designation");
            return;
        }
        else if (!values.message.trim()) {
            setUserAlert('Please enter the Message');
            return;
        }

        let data = {
            name: values.firstname.trim(),
            email: values.email.trim(),
            phoneNo: values.phone.trim(),
            message: values.message,
            industry: values.industry,
            organizationType: values.organizationType,
            designation: values.designation,
            location: window.location.href,
            page: props.contactUsModal.page ?? '',
            element: props.contactUsModal.element ?? ''
        };

        if(window.grecaptcha != null) {
            try {
                window.grecaptcha.execute(reCaptchaV3SiteKey, { action: "contactus"}).then((token) => {
                    data.token = token;
                    submitContactUs(data);
                }).catch((e) => {
                    console.log("Error while getting captcha token");
                    submitContactUs(data);
                });
            } catch(e) {
                console.log(e);
                submitContactUs(data);
            };
        } else {
            submitContactUs(data);
        } 
    }
    
    const submitContactUs = (data) => {
        return siteService.contactus(data)
        .then((result) => { 
            if(result.success) {
                toast.success('Submitted successfully.');
                setValues(initialValues);
                showContactUsModal(false);
            } else {
                setResponse(result);
                renderCaptchaV2();
            }
        })
        .catch(error => {
            console.log('Error in Contact us Form Submission', error);
            toast.error('Submission failed! Please try again.');
        });
    };

    const renderCaptchaV2 = () => {
         loadRecaptchaScript("v2").then((grecaptcha) => {
            grecaptcha.render(captchaRef.current, {
                sitekey: reCaptchaV2SiteKey,
                callback: (token) => {
                    onV2Success(token);
                },
                'error-callback': resetCaptcha,
                'expired-callback': resetCaptcha
            });
        }).catch(() => {
            showContactUsModal(false);
        });
    };

    const resetCaptcha = () => {
        if(window.grecaptcha) {
            window.grecaptcha.reset();
        }
    };

    const onV2Success = (token) => {
        const { id, guid } = responseRef.current;
        const request = {id: id, guid: guid, token: token};
        siteService.validateContactUs(request).then((result) => {
            if(result.success) {
                toast.success("Successfully submitted the request. You will hear from us soon.");
            } else {
                toast.error("Unable to validate the Captcha");
            }
            showContactUsModal(false);
        }).catch((error) => {
            console.log(error);
            toast.error("Unable to validate the Captcha");
        })
    };

    return (
        <>
            <Modal
                show={() => showContactUsModal(true)}
                onHide={() => showContactUsModal(false)}
                backdrop="static"
                keyboard={false}
                dialogClassName={styles["contact-modal-content"]}
                className={styles["contact-modal"]}
            >    
                <Modal.Body>
                    <span className={styles.close} onClick={() => showContactUsModal(false)}>&times;</span>
                    <div className={styles["form-container"]}>
                        <div className={styles["form-header"]}>
                            <div className={styles.header}>
                                <h3 className={styles.h3}>
                                    <strong><img className={styles.logo} src={DDLogoDark} /></strong></h3>
                                <h2 className={styles["form-header-title"]}>CONTACT US</h2>
                            </div>
                            <p className={styles.message}><strong>{props.contactUsModal.greetingMgs ?? 'We\'d love to hear from you!'}</strong><br />
                            </p>
                        </div>

                        <div>
                            <div className={styles.row}>
                                <input type="text" placeholder="Name *" required="" name="firstname" value={values.firstname} onChange = {(e) => handleInputChange(e)}/>
                                <select required="" name="industry" value={values.industry} onChange={(e) => handleInputChange(e)}>
                                    <option value="">Industry *</option>
                                    <option value="Agriculture">Agriculture</option>
                                    <option value="Economic Development">Economic Development</option>
                                    <option value="Engineering and Construction">Engineering and Construction</option>
                                    <option value="Environment and Natural Resources">Environment and Natural Resources</option>
                                    <option value="Highways and Roads">Highways and Roads</option>
                                    <option value="Insurance">Insurance</option>
                                    <option value="Law Enforcement and Justice">Law Enforcement and Justice</option>
                                    <option value="Mining">Mining</option>
                                    <option value="National Security">National Security</option>
                                    <option value="Oil and Gas">Oil and Gas</option>
                                    <option value="Public Administration and Policy">Public Administration and Policy</option>
                                    <option value="Railroad">Railroad</option>
                                    <option value="Real Estate">Real Estate</option>
                                    <option value="Urban and Regional Planning">Urban and Regional Planning</option>
                                    <option value="Others">Others</option>
                                </select>
                            </div>

                            <div className={styles.row}>
                                <input type="email" placeholder="Email *" required="" name="email" value={values.email} onChange = {(e) => handleInputChange(e)} />
                                <select required="" name="organizationType" value={values.organizationType} onChange={(e) => handleInputChange(e)}>
                                    <option value="">Organization Type *</option>
                                    <option value="City Government">City Government</option>
                                    <option value="State Government">State Government</option>
                                    <option value="Regional Government">Regional Government</option>
                                    <option value="National Government">National Government</option>
                                    <option value="Nonprofit and NGOs">Nonprofit and NGOs</option>
                                    <option value="Private/Business">Private/Business</option>
                                    <option value="Others">Others</option>
                                </select>
                            </div>

                            <div className={styles.row}>
                                <input type="tel" placeholder="Phone" name="phone"  value={values.phone} onChange = {(e) => handleInputChange(e)} />
                                <select required="" name="designation" value={values.designation} onChange={(e) => handleInputChange(e)}>
                                    <option value="">Role / Designation *</option>
                                    <option value="Director">Director</option>
                                    <option value="Department Head">Department Head</option>
                                    <option value="Team Lead">Team Lead</option>
                                    <option value="Domain Expert">Domain Expert</option>
                                    <option value="Technologist">Technologist</option>
                                    <option value="Procurement Officer">Procurement Officer</option>
                                    <option value="Support Staff">Support Staff</option>
                                    <option value="Others">Others</option>
                                </select>
                            </div>

                            <textarea placeholder="Message" name="message" value={values.message} onChange = {(e) => handleInputChange(e)}></textarea>
                            {
                                !!userAlert && 
                                <div className={styles["user-alert"]}>{userAlert}</div>
                            }
                            <button type="submit" className={styles["submit-btn"]} onClick={handleSubmit}>Submit</button>
                        </div>

                        <small className={styles.footerMessage}>
                        By providing your details above, you agree to receive communications from DataDeli.
                        You can unsubscribe at any time. DataDeli does not share contact information with any
                        external parties. Your information is private and will be used only to contact you
                        about our products and services. View our <a target="_blank" href="https://www.spaceaeye.ai/privacy-policy.html">Privacy Policy</a>.
                        </small>
                    </div>
                    <div ref={captchaRef}></div>
                </Modal.Body>
            </Modal>
        </>
    );
}

export default ContactUsModal;